import { createLogger } from "@/lib/logger";
import { supabase } from "@/lib/supabaseClient";
import { useLocalParticipant } from "@livekit/components-react";
import * as React from "react";

const logger = createLogger("speaking-memory-capture");
const MAX_BUFFER_MS = 3 * 60 * 1000;
const MOBILE_SAFARI_MAX_BUFFER_MS = 45 * 1000;
const CLIP_LOOKBACK_MS = 7000;
const CLIP_LEAD_MS = 1000;
const MAX_UPLOADS_PER_SESSION = 3;
const PCM_BUFFER_SIZE = 4096;

type ArtifactMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  firstReceivedTime: number;
};

type UploadParams = {
  conversationId: string;
  languageName: string;
  languageLevel: string;
  messages: ArtifactMessage[];
  accessToken?: string;
};

type PcmChunk = {
  samples: Float32Array;
  startedAtMs: number;
  endedAtMs: number;
};

function blobToBase64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Failed to convert audio blob to base64"));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

function buildUploadTargets(messages: ArtifactMessage[]) {
  const recentMessages = [...messages]
    .filter((message) => message.role === "user" && !message.id.startsWith("typed_user_"))
    .filter((message) => message.text.trim().length >= 6)
    .sort((a, b) => b.firstReceivedTime - a.firstReceivedTime);

  const selected: Array<{
    message: ArtifactMessage;
    tutorFeedback: string | null;
  }> = [];
  const seenTexts = new Set<string>();

  for (const message of recentMessages) {
    const dedupeKey = message.text.trim().toLowerCase();
    if (seenTexts.has(dedupeKey)) continue;
    seenTexts.add(dedupeKey);

    const tutorFeedback =
      messages.find(
        (candidate) =>
          candidate.role === "assistant" &&
          candidate.firstReceivedTime > message.firstReceivedTime &&
          candidate.firstReceivedTime - message.firstReceivedTime < 30000
      )?.text || null;

    selected.push({ message, tutorFeedback });
    if (selected.length >= MAX_UPLOADS_PER_SESSION) break;
  }

  return selected.reverse();
}

function encodeWav(samples: Float32Array, sampleRate: number) {
  const bytesPerSample = 2;
  const blockAlign = bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, samples[i] || 0));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: "audio/wav" });
}

function mergeFloat32Arrays(chunks: Float32Array[]) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Float32Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }

  return merged;
}

function getPcmBufferWindowMs() {
  if (typeof navigator === "undefined") {
    return MAX_BUFFER_MS;
  }

  const userAgent = navigator.userAgent;
  const isMobileSafari =
    /iP(hone|ad|od)/.test(userAgent) &&
    /Safari/.test(userAgent) &&
    !/(CriOS|FxiOS|EdgiOS)/.test(userAgent);

  return isMobileSafari ? MOBILE_SAFARI_MAX_BUFFER_MS : MAX_BUFFER_MS;
}

function trimPcmChunksInPlace(chunks: PcmChunk[], cutoffMs: number) {
  let firstRetainedIndex = 0;
  while (firstRetainedIndex < chunks.length && chunks[firstRetainedIndex].endedAtMs < cutoffMs) {
    firstRetainedIndex += 1;
  }

  if (firstRetainedIndex > 0) {
    chunks.splice(0, firstRetainedIndex);
  }
}

export default function useSpeakingMemoryCapture(isActiveSession: boolean) {
  const { microphoneTrack } = useLocalParticipant();
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const sourceNodeRef = React.useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = React.useRef<ScriptProcessorNode | null>(null);
  const gainNodeRef = React.useRef<GainNode | null>(null);
  const sinkNodeRef = React.useRef<MediaStreamAudioDestinationNode | null>(null);
  const clonedTrackRef = React.useRef<MediaStreamTrack | null>(null);
  const pcmChunksRef = React.useRef<PcmChunk[]>([]);
  const uploadedMessageIdsRef = React.useRef<Set<string>>(new Set());
  const sampleRateRef = React.useRef<number>(48000);
  const [isRecorderReady, setIsRecorderReady] = React.useState(false);

  const stopCapture = React.useCallback(() => {
    processorNodeRef.current?.disconnect();
    sourceNodeRef.current?.disconnect();
    gainNodeRef.current?.disconnect();
    sinkNodeRef.current?.disconnect();
    processorNodeRef.current = null;
    sourceNodeRef.current = null;
    gainNodeRef.current = null;
    sinkNodeRef.current = null;
    setIsRecorderReady(false);

    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }

    if (clonedTrackRef.current) {
      clonedTrackRef.current.stop();
      clonedTrackRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    const mediaStreamTrack = (microphoneTrack?.track as any)?.mediaStreamTrack as
      | MediaStreamTrack
      | undefined;
    const AudioContextCtor =
      typeof window !== "undefined"
        ? window.AudioContext ||
          (window as typeof window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext
        : undefined;

    if (!isActiveSession || !mediaStreamTrack || !AudioContextCtor) {
      if (!isActiveSession) {
        stopCapture();
      }
      return;
    }

    if (audioContextRef.current) {
      return;
    }

    const clonedTrack = mediaStreamTrack.clone();
    const stream = new MediaStream([clonedTrack]);
    const audioContext = new AudioContextCtor();
    const sourceNode = audioContext.createMediaStreamSource(stream);
    const processorNode = audioContext.createScriptProcessor(PCM_BUFFER_SIZE, 1, 1);
    const gainNode = audioContext.createGain();
    const sinkNode = audioContext.createMediaStreamDestination();
    gainNode.gain.value = 0;

    sampleRateRef.current = audioContext.sampleRate;
    clonedTrackRef.current = clonedTrack;
    audioContextRef.current = audioContext;
    sourceNodeRef.current = sourceNode;
    processorNodeRef.current = processorNode;
    gainNodeRef.current = gainNode;
    sinkNodeRef.current = sinkNode;

    processorNode.onaudioprocess = (event) => {
      let samples: Float32Array;

      try {
        const input = event.inputBuffer.getChannelData(0);
        samples = new Float32Array(input);
      } catch (error) {
        logger.warn("Speaking memory PCM capture stopped after allocation failure", {
          error: error instanceof Error ? error.message : String(error),
          bufferedChunkCount: pcmChunksRef.current.length,
        });
        stopCapture();
        return;
      }

      const endedAtMs = Date.now();
      const startedAtMs = endedAtMs - (samples.length / sampleRateRef.current) * 1000;

      pcmChunksRef.current.push({
        samples,
        startedAtMs,
        endedAtMs,
      });

      const cutoff = endedAtMs - getPcmBufferWindowMs();
      trimPcmChunksInPlace(pcmChunksRef.current, cutoff);
    };

    sourceNode.connect(processorNode);
    processorNode.connect(gainNode);
    gainNode.connect(sinkNode);

    if (audioContext.state === "suspended") {
      void audioContext.resume().catch((error) => {
        logger.warn("Failed to resume speaking memory audio context", {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }

    logger.info("Speaking memory PCM capture started", {
      sampleRate: audioContext.sampleRate,
      bufferSize: PCM_BUFFER_SIZE,
    });
    setIsRecorderReady(true);

    return () => {
      if (audioContextRef.current === audioContext) {
        stopCapture();
      }
    };
  }, [isActiveSession, microphoneTrack, stopCapture]);

  const uploadRecentSpeakingMemories = React.useCallback(
    async ({
      conversationId,
      languageName,
      languageLevel,
      messages,
      accessToken,
    }: UploadParams) => {
      if (!conversationId || !languageName || !languageLevel) {
        logger.debug("Skipping speaking memory upload: missing session context", {
          hasConversationId: Boolean(conversationId),
          hasLanguageName: Boolean(languageName),
          hasLanguageLevel: Boolean(languageLevel),
        });
        return { uploaded: 0 };
      }

      if (!pcmChunksRef.current.length) {
        logger.debug("Skipping speaking memory upload: no PCM audio chunks available");
        return { uploaded: 0 };
      }

      const uploadTargets = buildUploadTargets(messages);
      logger.info("Speaking memory upload evaluation", {
        bufferedChunkCount: pcmChunksRef.current.length,
        candidateMessageCount: messages.length,
        eligibleTargetCount: uploadTargets.length,
        isRecorderReady,
        conversationId,
        languageName,
        sampleRate: sampleRateRef.current,
      });

      if (!uploadTargets.length) {
        logger.debug("Skipping speaking memory upload: no eligible user voice turns found");
        return { uploaded: 0 };
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const bearer = accessToken || session?.access_token;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (bearer) {
        headers.Authorization = `Bearer ${bearer}`;
      }

      let uploaded = 0;

      for (const target of uploadTargets) {
        if (uploadedMessageIdsRef.current.has(target.message.id)) {
          logger.debug("Skipping speaking memory upload: message already uploaded", {
            messageId: target.message.id,
          });
          continue;
        }

        const clipStart = target.message.firstReceivedTime - CLIP_LOOKBACK_MS;
        const clipEnd = target.message.firstReceivedTime + CLIP_LEAD_MS;

        const relevantSlices: Float32Array[] = [];

        for (const chunk of pcmChunksRef.current) {
          if (chunk.endedAtMs <= clipStart || chunk.startedAtMs >= clipEnd) {
            continue;
          }

          const chunkDurationMs = Math.max(1, chunk.endedAtMs - chunk.startedAtMs);
          const chunkSampleRate = chunk.samples.length / (chunkDurationMs / 1000);
          const effectiveSampleRate = Number.isFinite(chunkSampleRate)
            ? chunkSampleRate
            : sampleRateRef.current;

          const sliceStartMs = Math.max(0, clipStart - chunk.startedAtMs);
          const sliceEndMs = Math.min(chunkDurationMs, clipEnd - chunk.startedAtMs);
          const startIndex = Math.max(0, Math.floor((sliceStartMs / 1000) * effectiveSampleRate));
          const endIndex = Math.min(
            chunk.samples.length,
            Math.ceil((sliceEndMs / 1000) * effectiveSampleRate)
          );

          if (endIndex > startIndex) {
            relevantSlices.push(chunk.samples.slice(startIndex, endIndex));
          }
        }

        if (!relevantSlices.length) {
          logger.debug("Skipping speaking memory upload: no PCM slices for target", {
            messageId: target.message.id,
            transcript: target.message.text.slice(0, 80),
            clipStart,
            clipEnd,
          });
          continue;
        }

        const mergedSamples = mergeFloat32Arrays(relevantSlices);
        const clipBlob = encodeWav(mergedSamples, sampleRateRef.current);

        if (clipBlob.size < 2048) {
          logger.debug("Skipping speaking memory upload: clip too small", {
            messageId: target.message.id,
            clipSize: clipBlob.size,
            transcript: target.message.text.slice(0, 80),
          });
          continue;
        }

        const audioBase64 = await blobToBase64(clipBlob);
        logger.info("Speaking memory POST started", {
          messageId: target.message.id,
          transcript: target.message.text.slice(0, 80),
          clipSize: clipBlob.size,
          sampleCount: mergedSamples.length,
        });

        const response = await fetch("/api/progress/speaking-memory", {
          method: "POST",
          headers,
          body: JSON.stringify({
            conversationId,
            languageName,
            languageLevel,
            transcript: target.message.text,
            normalizedText: target.message.text,
            tutorFeedback: target.tutorFeedback,
            mimeType: "audio/wav",
            audioBase64,
            source: "session_end_auto_capture",
            startedAtMs: clipStart,
            durationMs: clipEnd - clipStart,
            metadata: {
              capture: "rolling_pcm_buffer",
              trigger: "session_end",
              sampleRate: sampleRateRef.current,
            },
          }),
        });

        if (response.ok) {
          uploadedMessageIdsRef.current.add(target.message.id);
          uploaded += 1;
          const json = await response.json().catch(() => null);
          logger.info("Speaking memory uploaded", {
            speakingEventId: json?.id || null,
            transcript: target.message.text.slice(0, 80),
            languageName,
            embeddingMode: json?.embeddingMode || null,
          });
        } else {
          logger.warn("Speaking memory upload failed", {
            status: response.status,
            messageId: target.message.id,
            transcript: target.message.text.slice(0, 80),
          });
        }
      }

      logger.info("Speaking memory upload batch complete", {
        uploaded,
        attempted: uploadTargets.length,
        languageName,
      });

      return { uploaded };
    },
    [isRecorderReady]
  );

  React.useEffect(() => {
    if (!isActiveSession) {
      uploadedMessageIdsRef.current.clear();
    }
  }, [isActiveSession]);

  React.useEffect(() => {
    return () => {
      stopCapture();
    };
  }, [stopCapture]);

  return {
    isRecorderReady,
    uploadRecentSpeakingMemories,
  };
}
